import { useState, useEffect } from "react";
import {
  Typography, Button, Stack, Box, Card, CardContent,
  IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, CircularProgress,
  MenuItem, Select, InputLabel, FormControl, Rating
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryIcon from "@mui/icons-material/Inventory";
import {
  collection, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/config";

interface ProductColor {
  name: string;
  hex: string;
}

interface DistripakoProduct {
  id?: string;
  name: string;
  category: string;
  categoryLabel: string;
  shortDesc: string;
  description: string;
  fabric: string;
  composition: string;
  basePrice: number;
  image: string;
  availableColors: ProductColor[];
  availableSizes: string[];
  features: string[];
  recommendedUse: string;
  popular: boolean;
  active: boolean;
  order: number;
}

const empty: DistripakoProduct = {
  name: "",
  category: "industrial",
  categoryLabel: "Dotación Industrial",
  shortDesc: "",
  description: "",
  fabric: "Drill Vulcano 100% Algodón",
  composition: "100% Algodón 240g/m²",
  basePrice: 45000,
  image: "",
  availableColors: [
    { name: "Azul Industrial", hex: "#1B3A6B" },
    { name: "Gris Cemento", hex: "#6B7280" }
  ],
  availableSizes: ["S", "M", "L", "XL", "XXL"],
  features: ["Costuras reforzadas", "Cintas reflectivas de alta visibilidad"],
  recommendedUse: "Trabajo pesado, minería, construcción y logística",
  popular: false,
  active: true,
  order: 0,
};

export default function DistripakoProductosPage() {
  const [items, setItems] = useState<DistripakoProduct[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<DistripakoProduct>(empty);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  // Aux state for arrays as strings in form
  const [featuresStr, setFeaturesStr] = useState("");
  const [sizesStr, setSizesStr] = useState("");

  useEffect(() => {
    // Escuchar categorías para el dropdown
    const unsubCats = onSnapshot(collection(db, "categorias"), (snap) => {
      const cats = snap.docs.map((d) => ({ id: d.id, name: d.data().name || d.id }));
      setCategories(cats);
    });

    // Escuchar productos web
    const unsubProds = onSnapshot(collection(db, "productos"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DistripakoProduct));
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(data);
      setLoading(false);
    });

    return () => {
      unsubCats();
      unsubProds();
    };
  }, []);

  const handleOpen = (item?: DistripakoProduct) => {
    if (item) {
      setCurrent({ ...item });
      setFeaturesStr((item.features || []).join("\n"));
      setSizesStr((item.availableSizes || []).join(", "));
    } else {
      setCurrent({ ...empty, order: items.length });
      setFeaturesStr(empty.features.join("\n"));
      setSizesStr(empty.availableSizes.join(", "));
    }
    setOpen(true);
  };

  const handleSave = async () => {
    const id = current.id || doc(collection(db, "productos")).id;
    const features = featuresStr.split("\n").map((f) => f.trim()).filter(Boolean);
    const availableSizes = sizesStr.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);

    await setDoc(doc(db, "productos", id), {
      ...current,
      id,
      features,
      availableSizes,
      updatedAt: serverTimestamp(),
      ...(current.id ? {} : { createdAt: serverTimestamp() }),
    }, { merge: true });
    setOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Eliminar este producto de la tienda/catálogo web?")) {
      await deleteDoc(doc(db, "productos", id));
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    try {
      const storageRef = ref(storage, `productos/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setCurrent((prev) => ({ ...prev, image: url }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>👕 Productos y Dotaciones Web</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona el catálogo de prendas, precios base y tallas que ve el cliente en el cotizador y catálogo
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
          sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>
          Nuevo Producto
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))" gap={2.5}>
          {items.map((item) => (
            <Card key={item.id} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
              {item.image && (
                <Box component="img" src={item.image} alt={item.name}
                  sx={{ width: "100%", height: 160, objectFit: "cover" }} />
              )}
              <CardContent sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                      <Typography fontWeight={800} fontSize="1.05rem">{item.name}</Typography>
                      <Chip label={item.active ? "Activo" : "Inactivo"} size="small"
                        color={item.active ? "success" : "default"} />
                      {item.popular && <Chip label="⭐ Destacado" size="small" color="primary" />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                      {item.categoryLabel || item.category}
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight={800} my={1}>
                      ${Number(item.basePrice || 0).toLocaleString("es-CO")} COP
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }} noWrap>
                      {item.shortDesc || item.description}
                    </Typography>
                    <Stack direction="row" gap={0.5} mt={1.5} flexWrap="wrap">
                      {(item.availableSizes || []).map((sz) => (
                        <Chip key={sz} label={sz} size="small" variant="outlined" sx={{ fontSize: "0.7rem", height: 22 }} />
                      ))}
                    </Stack>
                  </Box>
                  <Stack>
                    <IconButton size="small" onClick={() => handleOpen(item)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id!)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && (
            <Box gridColumn="1/-1" textAlign="center" py={8} color="text.secondary">
              <InventoryIcon sx={{ fontSize: 64, opacity: 0.2, mb: 2, display: "block", mx: "auto" }} />
              <Typography>No hay productos registrados en el catálogo web.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {current.id ? "✏️ Editar Producto Web" : "✨ Nuevo Producto Web"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Nombre del Producto" value={current.name} fullWidth
                onChange={(e) => setCurrent((p) => ({ ...p, name: e.target.value }))} />
              <FormControl fullWidth>
                <InputLabel>Categoría Web</InputLabel>
                <Select
                  value={current.category}
                  label="Categoría Web"
                  onChange={(e) => {
                    const selectedCat = categories.find((c) => c.id === e.target.value);
                    setCurrent((p) => ({
                      ...p,
                      category: e.target.value,
                      categoryLabel: selectedCat ? selectedCat.name : e.target.value
                    }));
                  }}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                  {categories.length === 0 && (
                    <MenuItem value="industrial">Industrial</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Precio Base (COP)" type="number" value={current.basePrice} fullWidth
                onChange={(e) => setCurrent((p) => ({ ...p, basePrice: Number(e.target.value) }))} />
              <TextField label="Orden de Aparición" type="number" value={current.order} fullWidth
                onChange={(e) => setCurrent((p) => ({ ...p, order: Number(e.target.value) }))} />
            </Stack>

            <TextField label="Descripción Corta" value={current.shortDesc} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, shortDesc: e.target.value }))} />

            <TextField label="Descripción Completa" value={current.description} fullWidth multiline rows={2}
              onChange={(e) => setCurrent((p) => ({ ...p, description: e.target.value }))} />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Tela / Material" value={current.fabric} fullWidth
                onChange={(e) => setCurrent((p) => ({ ...p, fabric: e.target.value }))} />
              <TextField label="Composición" value={current.composition} fullWidth
                onChange={(e) => setCurrent((p) => ({ ...p, composition: e.target.value }))} />
            </Stack>

            <TextField label="Uso Recomendado" value={current.recommendedUse} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, recommendedUse: e.target.value }))} />

            <TextField label="Tallas Disponibles (separadas por coma)" value={sizesStr} fullWidth
              placeholder="S, M, L, XL, XXL"
              onChange={(e) => setSizesStr(e.target.value)} />

            <TextField label="Características / Beneficios (uno por línea)" value={featuresStr} fullWidth multiline rows={3}
              placeholder="Costuras reforzadas&#10;Cintas reflectivas&#10;Resistente al desgaste"
              onChange={(e) => setFeaturesStr(e.target.value)} />

            {/* Image */}
            <Stack direction="row" spacing={2} alignItems="center">
              {current.image && (
                <Box component="img" src={current.image} sx={{ width: 100, height: 75, objectFit: "cover", borderRadius: 2 }} />
              )}
              <Button component="label" variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : undefined}
                disabled={uploading} sx={{ borderRadius: 2 }}>
                {uploading ? "Subiendo..." : "Subir Foto del Producto"}
                <input hidden type="file" accept="image/*"
                  onChange={(e) => e.target.files && uploadImage(e.target.files[0])} />
              </Button>
            </Stack>

            <Stack direction="row" spacing={3}>
              <FormControlLabel
                control={<Switch checked={current.active} onChange={(e) => setCurrent((p) => ({ ...p, active: e.target.checked }))} />}
                label="Producto Activo en Web"
              />
              <FormControlLabel
                control={<Switch checked={current.popular} onChange={(e) => setCurrent((p) => ({ ...p, popular: e.target.checked }))} />}
                label="Marcar como Destacado ⭐"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>
            Guardar Producto
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
